'use client';

import { useRef } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import type { Sport } from '@sharpr/types';
import { invalidatePricedSurfaces } from '@/lib/pricedSurfaces';

/**
 * Polls the legacy `/api/odds-version` transport every ~5 seconds. The value
 * is now the sport's shared priced-surface version: odds writes, projection
 * releases, and analyst pricing decisions all publish through it.
 *
 * The first poll records a baseline without invalidating so newly-mounted
 * clients do not immediately duplicate their initial reads.
 */
export function usePricedSurfaceVersion(sport: Sport) {
  const queryClient = useQueryClient();
  const lastSeen = useRef<string | null>(null);

  useQuery({
    queryKey: ['odds-version', sport],
    queryFn: async () => {
      const res = await fetch(`/api/odds-version?sport=${sport}`);
      if (!res.ok) throw new Error(`odds-version failed: ${res.status}`);
      const { version } = (await res.json()) as { version: string };

      if (lastSeen.current !== null && lastSeen.current !== version) {
        invalidatePricedSurfaces(queryClient, sport);
      }
      lastSeen.current = version;
      return version;
    },
    refetchInterval: () => 4500 + Math.random() * 1000,
    staleTime: 0,
    gcTime: Infinity,
  });
}
